import { useState, type ReactNode } from "react";
import type { Scope } from "../api";
import { Modal } from "./Modal";
import { Btn, Field, Input, Select } from "./ui";

export type RepeatMode = "unica" | "parcelada" | "fixa";
const PRESETS: [string, number][] = [["Mensal", 1], ["Bimestral", 2], ["Trimestral", 3], ["Semestral", 6], ["Anual", 12]];

/** Estado e campos de Repetição (Única / Parcelada / Fixa), iguais para lançamentos e transferências. */
export function useRepeat() {
  const [mode, setMode] = useState<RepeatMode>("unica");
  const [everyMonths, setEveryMonths] = useState(1);
  const [installments, setInstallments] = useState("2");
  const [startNumber, setStartNumber] = useState("1");
  const [amountIsInstallment, setAmountIsInstallment] = useState(true);
  const [limited, setLimited] = useState(false);
  const [occurrences, setOccurrences] = useState("18");
  const [custom, setCustom] = useState(false);

  /** Monta o `repeat` enviado à API, ou a mensagem de erro de validação. */
  function build(): { repeat?: Record<string, unknown>; error?: string } {
    if (mode === "unica") return {};
    if (!Number.isInteger(everyMonths) || everyMonths < 1 || everyMonths > 24) return { error: "O intervalo deve ser de 1 a 24 meses." };
    const repeat: Record<string, unknown> = { mode, everyMonths };
    if (mode === "parcelada") {
      const n = Number(installments), st = Number(startNumber);
      if (!Number.isInteger(n) || n < 2 || n > 120) return { error: "Informe o número de parcelas (de 2 a 120)." };
      if (!Number.isInteger(st) || st < 1 || st > n) return { error: "A parcela inicial deve estar entre 1 e o número de parcelas." };
      Object.assign(repeat, { installments: n, startNumber: st, amountIsInstallment });
    } else if (limited) {
      const o = Number(occurrences);
      if (!Number.isInteger(o) || o < 2 || o > 120) return { error: "Informe o número de ocorrências (de 2 a 120)." };
      repeat.occurrences = o;
    }
    return { repeat };
  }

  const fields = (amount: { norm: string | null; format: (v: number) => string }): ReactNode => (
    <>
      <div className="grid2">
        <Field label="Repetição">
          <Select value={mode} onChange={(e) => setMode(e.target.value as RepeatMode)} name="repeatMode" data-repeat>
            <option value="unica">Única</option><option value="parcelada">Parcelada</option><option value="fixa">Fixa</option>
          </Select>
        </Field>
        {mode !== "unica" && (
          <Field label="Periodicidade" required>
            <Select value={PRESETS.find(([, m]) => m === everyMonths)?.[1] ?? "custom"} onChange={(e) => { if (e.target.value === "custom") setCustom(true); else setEveryMonths(Number(e.target.value)); }} name="periodicity">
              {PRESETS.map(([l, m]) => <option key={m} value={m}>{l}</option>)}
              <option value="custom">A cada n meses…</option>
            </Select>
          </Field>
        )}
      </div>
      {mode === "parcelada" && (
        <div className="grid2">
          <Field label="Número de parcelas" required><Input type="number" min={2} max={120} value={installments} onChange={(e) => setInstallments(e.target.value)} name="installments" /></Field>
          <div style={{ alignSelf: "end", paddingBottom: 8 }}><button type="button" className="link" onClick={() => setCustom((v) => !v)} data-repeat-custom>{custom ? "Ocultar opções" : "Personalizar"}</button></div>
        </div>
      )}
      {mode === "fixa" && (
        <>
          <label className="check"><input type="checkbox" checked={limited} onChange={(e) => setLimited(e.target.checked)} name="limited" data-fixa-limited /> Definir número de ocorrências</label>
          {limited && <Field label="Número de ocorrências" required><Input type="number" min={2} max={120} value={occurrences} onChange={(e) => setOccurrences(e.target.value)} name="occurrences" data-occurrences /></Field>}
          <p className="rep-info">{limited
            ? `Fixa com ${occurrences || "n"} ocorrências: todas são criadas agora e a série termina na última (não renova).`
            : "Fixa sem número definido: cria 12 meses de uma vez e renova sozinha ao final do período. Para encerrar, cancele “esta e as seguintes”."}</p>
        </>
      )}
      {mode !== "unica" && custom && (
        <div className="rep-panel" data-repeat-panel>
          <div className="grid2">
            <Field label="Repete-se a cada n meses" required><Input type="number" min={1} max={24} value={everyMonths} onChange={(e) => setEveryMonths(Number(e.target.value))} name="everyMonths" /></Field>
            {mode === "parcelada" && <Field label="Parcela inicial" required><Input type="number" min={1} value={startNumber} onChange={(e) => setStartNumber(e.target.value)} name="startNumber" /></Field>}
          </div>
          {mode === "parcelada" && <label className="check"><input type="checkbox" checked={amountIsInstallment} onChange={(e) => setAmountIsInstallment(e.target.checked)} name="amountIsInstallment" /> O valor informado é o valor de cada parcela (desmarque para dividir o valor total pelo número de parcelas)</label>}
        </div>
      )}
      {mode === "parcelada" && (() => {
        const n = Number(installments), st = Number(startNumber);
        if (!Number.isInteger(n) || n < 2 || !Number.isInteger(st) || st < 1 || st > n || amount.norm === null) return null;
        const v = Number(amount.norm), each = amountIsInstallment ? v : v / n, rows = n - st + 1;
        return <p className="rep-info" data-repeat-preview>Serão criadas {rows} {rows === 1 ? "parcela" : "parcelas"} ({st}/{n} a {n}/{n}) de {amount.format(each)}{amountIsInstallment ? "" : " (aprox., o total é dividido exatamente)"}, uma a cada {everyMonths} {everyMonths === 1 ? "mês" : "meses"}.</p>;
      })()}
    </>
  );
  return { mode, build, fields };
}

/** Pergunta obrigatória ao salvar/cancelar uma ocorrência de série: só esta ou esta e as seguintes. Nunca altera as anteriores. */
export function ScopeDialog({ title, what, onPick, onClose, busy }: { title: string; what: string; onPick: (s: Scope) => void; onClose: () => void; busy?: boolean }) {
  return (
    <Modal title={title} onClose={onClose}>
      <p data-scope-dialog>{what}</p>
      <p className="small muted">As ocorrências anteriores, já realizadas ou canceladas nunca são alteradas.</p>
      <div className="scope-choices">
        <Btn onClick={() => onPick("this")} disabled={busy} data-scope-this>Somente esta</Btn>
        <Btn onClick={() => onPick("following")} disabled={busy} data-scope-following>Esta e as seguintes</Btn>
        <Btn variant="ghost" onClick={onClose} disabled={busy}>Voltar</Btn>
      </div>
    </Modal>
  );
}
